// gatsby-browser.js

import React from "react";
import { captureTrackingParameters } from "./src/utils/tracking";
import CartProvider from "./src/components/context";

// Wrap every page in the cart provider so cart/checkout can read and mutate
// cart state. Defined once and shared with gatsby-ssr.js for SSR parity.
export const wrapRootElement = ({ element }) => (
  <CartProvider>{element}</CartProvider>
);

// ---------------------------------------------------------------------------
// CallRail Dynamic Number Insertion (DNI)
// ---------------------------------------------------------------------------
//
// CallRail's swap.js is loaded directly here (instead of through Google Tag
// Manager) so that it loads exactly once and we can re-trigger the number
// swap after Gatsby's client-side (SPA) navigations.
//
// Verified against this account's actual script
// (https://cdn.callrail.com/companies/412362498/6695a2e2a1ff3e2838ff/12/swap.js):
//   - The global it installs is `window.CallTrk`.
//   - Its public re-scan API is `CallTrk.swap(rootElement)` — rootElement
//     defaults to document.body. It resets Debug.doneSwaps and re-runs
//     startSourceSwap + checkSessionSwap over the DOM, i.e. a full re-scan of
//     the current page's phone numbers and tel: links.
//   - It contains a MutationObserver, but it only activates when the account
//     config flag `mutation_observer` is enabled; for this account it is not,
//     which is why SPA navigations were not being re-swapped automatically.
//
// swap.js runs its own scan once on load (covering the initial page). We only
// call CallTrk.swap() again on subsequent client-side route changes.

const CALLRAIL_SCRIPT_ID = "callrail-swap-script";
const CALLRAIL_SCRIPT_SRC =
  "https://cdn.callrail.com/companies/412362498/6695a2e2a1ff3e2838ff/12/swap.js";

// Module-scoped state shared between onClientEntry and onRouteUpdate.
let callRailReady = false; // swap.js has fired its load event
let pendingSwap = false; // a route change asked for a swap before it was ready
let lastSwappedPath = null; // de-dupe swaps for the same path

// Run CallRail's re-scan against the current DOM. Safe to call repeatedly:
// CallRail resets its own doneSwaps tracking each call, and it reuses the
// already-loaded tracking session (no new script, no new session).
const runCallRailSwap = (path) => {
  try {
    if (window.CallTrk && typeof window.CallTrk.swap === "function") {
      window.CallTrk.swap();
      lastSwappedPath = path;
      return true;
    }
    // Legacy fallback for older CallRail installs; this account uses CallTrk,
    // so this branch is defensive only.
    if (window.__ctm && typeof window.__ctm.swap === "function") {
      window.__ctm.swap();
      lastSwappedPath = path;
      return true;
    }
  } catch (e) {
    // A tracking swap must never break navigation.
  }
  return false;
};

// Ask for a swap for the given path. Runs immediately if CallRail is ready,
// otherwise records that a swap is pending so it can run once swap.js loads.
// This handles the race where a visitor navigates before swap.js finishes
// loading — no polling, no timers.
const requestCallRailSwap = (path) => {
  if (typeof window === "undefined") return;
  if (path === lastSwappedPath) return; // already swapped for this path

  const doSwap = () => {
    if (callRailReady) {
      runCallRailSwap(path);
    } else {
      // swap.js not loaded yet: remember that the current route needs a swap.
      // flushPendingSwap (fired on the script's load event) will handle it.
      pendingSwap = true;
    }
  };

  // Wait for the next frame(s) so React has committed the new page's DOM
  // (the new phone-number elements) before CallRail re-scans.
  if (typeof window.requestAnimationFrame === "function") {
    window.requestAnimationFrame(() => window.requestAnimationFrame(doSwap));
  } else {
    doSwap();
  }
};

// Called once swap.js has loaded. swap.js already scanned the current DOM on
// load; we only need an extra swap if a navigation happened while it loaded.
const flushPendingSwap = () => {
  callRailReady = true;
  if (pendingSwap) {
    pendingSwap = false;
    const currentPath =
      typeof window !== "undefined" ? window.location.pathname : null;
    runCallRailSwap(currentPath);
  }
};

// Inject swap.js exactly once. Idempotent via the element id so an accidental
// second call (or fast refresh in dev) cannot create a duplicate script or a
// duplicate CallRail session.
const loadCallRail = () => {
  if (typeof document === "undefined") return;

  const existing = document.getElementById(CALLRAIL_SCRIPT_ID);
  if (existing) {
    // Already present. If it has already loaded, mark ready.
    if (existing.getAttribute("data-loaded") === "true") {
      callRailReady = true;
    }
    return;
  }

  const script = document.createElement("script");
  script.id = CALLRAIL_SCRIPT_ID;
  script.src = CALLRAIL_SCRIPT_SRC;
  script.async = true;
  script.type = "text/javascript";

  script.addEventListener("load", () => {
    script.setAttribute("data-loaded", "true");
    flushPendingSwap();
  });
  script.addEventListener("error", () => {
    // Leave callRailReady false; nothing to swap if the script failed to load.
  });

  document.head.appendChild(script);
};

// Load CallRail once, as early as the client boots.
export const onClientEntry = () => {
  // Capture attribution from the landing URL at the earliest point, before any
  // component mounts, so a direct homepage -> Designer click already has it.
  captureTrackingParameters();
  loadCallRail();
};

export const onRouteUpdate = ({ location, prevLocation }) => {
  // 1. Capture UTM from URL and store in session
  const searchParams = new URLSearchParams(location.search);
  const utmParams = [];
  for (const [key, value] of searchParams.entries()) {
    if (key.startsWith('utm_')) {
      utmParams.push(`${key}=${value}`);
    }
  }

  // Store UTM params in sessionStorage if available (combined string used to
  // append attribution to outbound sensei3d links below).
  if (utmParams.length > 0) {
    sessionStorage.setItem('utmParams', utmParams.join('&'));
  }

  // Capture individual attribution keys — UTM fields plus the ad click IDs
  // (gclid / gbraid / wbraid) — so they survive internal navigation and reach
  // the CRM with the lead regardless of which page the form sits on. These are
  // the exact keys src/utils/submitLead.js reads when building the payload.
  // Only write when the URL actually carries a value; never overwrite an
  // existing stored value with an empty one.
  [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'gclid',
    'gbraid',
    'wbraid',
  ].forEach(key => {
    const value = searchParams.get(key);
    if (value) {
      sessionStorage.setItem(key, value);
    }
  });

  // 2. Add UTM to sensei3d links from sessionStorage
  const updateSenseiLinks = () => {
    const storedUtm = sessionStorage.getItem('utmParams');
    if (storedUtm) {
      const links = document.querySelectorAll('a[href*="sensei3d"]');
      links.forEach(link => {
        const currentUrl = link.getAttribute('href');
        if (!currentUrl.includes(storedUtm)) {
          const newUrl = currentUrl.includes('?')
            ? `${currentUrl}&${storedUtm}`
            : `${currentUrl}?${storedUtm}`;
          link.setAttribute('href', newUrl);
        }
      });
    }
  };

  // Run update on page load and route change
  if (document.readyState === 'complete') {
    updateSenseiLinks();
  } else {
    window.addEventListener('load', updateSenseiLinks);
  }

  // 3. Re-run CallRail DNI after client-side navigation.
  //
  // prevLocation is null on the first (hard) page load — there, swap.js runs
  // its own scan on load, so we skip and only re-trigger on genuine SPA
  // navigations (including Back/Forward, which also fire onRouteUpdate).
  if (prevLocation) {
    requestCallRailSwap(location.pathname);
  }
};
